import { useState, useCallback } from 'react';
import { Users, Loader2, X, Clock, Info, CheckCircle2, AlertCircle, XCircle } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogBody,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from '@/components/ui/use-toast';
import { api } from '@/lib/api';
import { useFindTime } from '../hooks/useFindTime';
import type { FreeSlot, TimeFormat, FindTimeParticipantResult } from '../types';

interface FindTimeDialogProps {
  open: boolean;
  onClose: () => void;
  onSelectSlot: (startAt: string, endAt: string) => void;
  timeFormat?: TimeFormat;
}

const DURATION_OPTIONS = [
  { label: '15 min', value: 15 },
  { label: '30 min', value: 30 },
  { label: '45 min', value: 45 },
  { label: '1 hour', value: 60 },
  { label: '1.5 hours', value: 90 },
  { label: '2 hours', value: 120 },
];

function formatSlotTime(iso: string, timeFormat: TimeFormat = '12h'): string {
  const d = new Date(iso);
  return d.toLocaleString(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: timeFormat === '12h',
  });
}

function formatWindow(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
  });
}

function ParticipantStatusRow({ p }: { p: FindTimeParticipantResult }) {
  const Icon =
    p.status === 'available' ? CheckCircle2 : p.status === 'error' ? XCircle : AlertCircle;
  const color =
    p.status === 'available'
      ? 'text-green-600'
      : p.status === 'error'
        ? 'text-destructive'
        : 'text-muted-foreground';
  return (
    <div className="flex items-start gap-2 text-xs">
      <Icon className={`h-3.5 w-3.5 mt-0.5 shrink-0 ${color}`} />
      <div className="min-w-0">
        <div>
          <span className="font-medium">@{p.username}</span>
          {p.status === 'available' && p.effectiveRange && (
            <span className="text-muted-foreground ml-1">
              ({formatWindow(p.effectiveRange.from)} – {formatWindow(p.effectiveRange.to)})
            </span>
          )}
        </div>
        {p.note && <div className="text-muted-foreground">{p.note}</div>}
      </div>
    </div>
  );
}

export function FindTimeDialog({
  open,
  onClose,
  onSelectSlot,
  timeFormat = '12h',
}: FindTimeDialogProps) {
  const { result, loading, error, findTime, reset } = useFindTime();

  const [participants, setParticipants] = useState<string[]>([]);
  const [usernameInput, setUsernameInput] = useState('');
  const [durationMinutes, setDurationMinutes] = useState(60);

  // Default: next 7 days
  const defaultStart = new Date();
  defaultStart.setHours(0, 0, 0, 0);
  const defaultEnd = new Date(defaultStart);
  defaultEnd.setDate(defaultEnd.getDate() + 7);

  const [rangeStart, setRangeStart] = useState(defaultStart.toISOString().slice(0, 10));
  const [rangeEnd, setRangeEnd] = useState(defaultEnd.toISOString().slice(0, 10));

  const handleAddParticipant = useCallback(async () => {
    const username = usernameInput.trim();
    if (!username) return;

    if (participants.includes(username)) {
      toast({ title: 'Already added', variant: 'destructive' });
      return;
    }

    // Pre-validate the username so a typo doesn't quietly land on the
    // results screen as "unknown-no-consent". The search resolver is
    // opaque (not-found and not-allowed both return null), so the toast
    // wording stays generic.
    try {
      const { user } = await api.searchCalendarUsers(username);
      if (!user) {
        toast({ title: `User "${username}" not found`, variant: 'destructive' });
        return;
      }
      setParticipants((prev) => [...prev, username]);
      setUsernameInput('');
    } catch {
      toast({ title: 'Search failed', variant: 'destructive' });
    }
  }, [usernameInput, participants]);

  const handleRemoveParticipant = useCallback((username: string) => {
    setParticipants((prev) => prev.filter((p) => p !== username));
  }, []);

  const handleFind = useCallback(async () => {
    if (participants.length === 0) {
      toast({ title: 'Add at least one participant', variant: 'destructive' });
      return;
    }

    try {
      await findTime(
        participants,
        new Date(rangeStart).toISOString(),
        new Date(rangeEnd + 'T23:59:59').toISOString(),
        durationMinutes,
      );
    } catch (err) {
      toast({
        title: 'Failed to find times',
        description: err instanceof Error ? err.message : 'Unknown error',
        variant: 'destructive',
      });
    }
  }, [participants, rangeStart, rangeEnd, durationMinutes, findTime]);

  const handleSelectSlot = useCallback(
    (slot: FreeSlot) => {
      onSelectSlot(slot.startAt, slot.endAt);
      onClose();
    },
    [onSelectSlot, onClose],
  );

  const handleClose = useCallback(() => {
    reset();
    onClose();
  }, [reset, onClose]);

  const availableCount = result?.participants.filter((p) => p.status === 'available').length ?? 0;
  const totalCount = result?.participants.length ?? 0;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && handleClose()}>
      <DialogContent className="sm:max-w-lg max-h-[80vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Users className="h-4 w-4" />
            Find a Time
          </DialogTitle>
        </DialogHeader>

        <DialogBody>
          <div className="flex items-start gap-2 rounded-md bg-muted/50 border px-3 py-2 text-xs text-muted-foreground">
            <Info className="h-3.5 w-3.5 mt-0.5 shrink-0" />
            <span>
              Find a time that works for everyone. Each participant must have shared their calendar
              with you — availability is limited to the window they consented to.
            </span>
          </div>

          <div className="space-y-4">
            {/* Participants */}
            <div className="space-y-2">
              <Label>Participants</Label>
              <div className="flex gap-2">
                <Input
                  placeholder="Enter username"
                  value={usernameInput}
                  onChange={(e) => setUsernameInput(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleAddParticipant()}
                />
                <Button variant="outline" size="sm" onClick={handleAddParticipant}>
                  Add
                </Button>
              </div>
              {participants.length > 0 && (
                <div className="flex flex-wrap gap-1.5">
                  {participants.map((p) => (
                    <span
                      key={p}
                      className="inline-flex items-center gap-1 text-xs bg-muted px-2 py-1 rounded"
                    >
                      @{p}
                      <button
                        onClick={() => handleRemoveParticipant(p)}
                        className="text-muted-foreground hover:text-foreground"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>

            {/* Date range */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="find-time-start">Start date</Label>
                <Input
                  id="find-time-start"
                  type="date"
                  value={rangeStart}
                  onChange={(e) => setRangeStart(e.target.value)}
                />
              </div>
              <div>
                <Label htmlFor="find-time-end">End date</Label>
                <Input
                  id="find-time-end"
                  type="date"
                  value={rangeEnd}
                  onChange={(e) => setRangeEnd(e.target.value)}
                />
              </div>
            </div>

            {/* Duration */}
            <div>
              <Label>Duration</Label>
              <Select
                value={String(durationMinutes)}
                onValueChange={(v) => setDurationMinutes(Number(v))}
              >
                <SelectTrigger className="w-36">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {DURATION_OPTIONS.map((opt) => (
                    <SelectItem key={opt.value} value={String(opt.value)}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Find button */}
            <Button onClick={handleFind} disabled={loading || participants.length === 0}>
              {loading ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}
              Find available slots
            </Button>

            {/* Error */}
            {error && <p className="text-sm text-destructive">{error}</p>}

            {/* Results */}
            {result && (
              <div className="space-y-3">
                {/* Per-participant status list */}
                <div className="space-y-1.5 border rounded-md p-3 bg-muted/30">
                  <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                    Participants ({availableCount} of {totalCount} scheduleable)
                  </div>
                  {result.participants.map((p) => (
                    <ParticipantStatusRow key={p.username} p={p} />
                  ))}
                </div>

                {/* Search window summary */}
                {result.searchWindow ? (
                  <div className="text-xs text-muted-foreground">
                    Searching {formatWindow(result.searchWindow.from)} –{' '}
                    {formatWindow(result.searchWindow.to)} (everyone&apos;s shared window).
                  </div>
                ) : (
                  <div className="text-xs text-muted-foreground">
                    No shared availability window — at least one participant&apos;s consent
                    doesn&apos;t overlap the requested range.
                  </div>
                )}

                {/* Slots */}
                <div className="space-y-2">
                  <div className="text-sm font-medium">
                    {result.freeSlots.length} available slot
                    {result.freeSlots.length !== 1 ? 's' : ''} found
                  </div>
                  {result.freeSlots.length > 0 ? (
                    <div className="max-h-48 overflow-y-auto space-y-1">
                      {result.freeSlots.slice(0, 50).map((slot, i) => (
                        <button
                          key={i}
                          onClick={() => handleSelectSlot(slot)}
                          className="w-full text-left text-sm px-3 py-2 rounded hover:bg-muted flex items-center gap-2 border"
                        >
                          <Clock className="h-3.5 w-3.5 text-muted-foreground" />
                          <span>
                            {formatSlotTime(slot.startAt, timeFormat)} &ndash;{' '}
                            {new Date(slot.endAt).toLocaleTimeString(undefined, {
                              hour: 'numeric',
                              minute: '2-digit',
                              hour12: timeFormat === '12h',
                            })}
                          </span>
                        </button>
                      ))}
                    </div>
                  ) : result.searchWindow ? (
                    <p className="text-sm text-muted-foreground">
                      No times work for everyone in this window. Try a wider date range or shorter
                      duration.
                    </p>
                  ) : null}
                </div>
              </div>
            )}
          </div>
        </DialogBody>
      </DialogContent>
    </Dialog>
  );
}
